import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { ReminderMethodType, getAllReminderMethods, translateReminderMethod } from '../../types/notificationMapping';
import { userPreferencesAtom } from '../../atoms/v3/userPreferencesAtoms';
import { BottomSheetModal } from './BottomSheetModal';

interface ChannelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  selectedChannels: ReminderMethodType[];
  onConfirm: (channels: ReminderMethodType[]) => void;
  title?: string;
  showUserPreferenceHint?: boolean;
}

/**
 * iOS 风格的推送渠道选择器组件
 */
export const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  isOpen,
  onClose,
  selectedChannels,
  onConfirm,
  title,
  showUserPreferenceHint = false
}) => {
  const { t } = useTranslation(['v3/tasks', 'v3/ios', 'common']);
  const userPreferences = useAtomValue(userPreferencesAtom);
  const [tempSelectedChannels, setTempSelectedChannels] = useState<ReminderMethodType[]>(selectedChannels);

  // 获取所有提醒方式配置，并进行国际化处理
  const reminderMethods = getAllReminderMethods().map(method => ({
    ...method,
    label: translateReminderMethod(method.value, t),
  }));

  // 当 props 变化时更新临时选择状态
  useEffect(() => {
    setTempSelectedChannels(selectedChannels);
  }, [selectedChannels, isOpen]);

  // 处理渠道切换
  const handleChannelToggle = (channelValue: ReminderMethodType) => {
    const method = reminderMethods.find(m => m.value === channelValue);
    if (!method?.available) return;

    if (tempSelectedChannels.includes(channelValue)) {
      // 如果只有一个方法，不允许取消选择
      if (tempSelectedChannels.length > 1) {
        setTempSelectedChannels(prev => prev.filter(c => c !== channelValue));
      }
    } else {
      setTempSelectedChannels(prev => [...prev, channelValue]);
    }
  };

  // 处理确认
  const handleConfirm = () => {
    onConfirm(tempSelectedChannels);
    onClose();
  };

  // 处理取消
  const handleCancel = () => {
    setTempSelectedChannels(selectedChannels); // 恢复原始状态
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title || t('v3/tasks:editors.channelSelector.title')}
      onConfirm={handleConfirm}
      confirmText={t('v3/tasks:editors.channelSelector.confirm')}
      cancelText={t('v3/tasks:editors.channelSelector.cancel')}
    >
      {/* 渠道选择区域 */}
      <div className="p-4">
          <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
            {t('v3/tasks:editors.channelSelector.description')}
          </p>

          {/* 用户偏好提示 */}
          {showUserPreferenceHint && userPreferences?.defaultReminderMethods && (
            <div className="mb-4 p-3 bg-ios-blue/10 dark:bg-ios-dark-blue/10 rounded-ios-md">
              <div className="text-xs text-ios-blue dark:text-ios-dark-blue font-medium mb-1">
                {t('v3/tasks:editors.channelSelector.userPreferenceTitle')}
              </div>
              <div className="text-xs text-ios-gray dark:text-ios-dark-gray">
                {userPreferences.defaultReminderMethods
                  .map(method => translateReminderMethod(method, t))
                  .join(t('common:notifications.separator', '、'))}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            {reminderMethods.map((method) => {
              const isSelected = tempSelectedChannels.includes(method.value);
              const isOnlySelected = tempSelectedChannels.length === 1 && isSelected;
              const isDisabled = isOnlySelected || !method.available;
              const isUserPreferred = userPreferences?.defaultReminderMethods?.includes(method.value);

              return (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => method.available && handleChannelToggle(method.value)}
                  disabled={isDisabled}
                  className={`
                    px-4 py-3 rounded-ios-lg text-sm font-medium transition-all duration-200 relative
                    ${isSelected && method.available
                      ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                      : 'bg-ios-gray6 dark:bg-ios-dark-gray5 text-ios-body dark:text-ios-dark-body'
                    }
                    ${isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:opacity-80 active:scale-95'
                    }
                  `}
                >
                  <div className="flex flex-col items-center">
                    <div className="flex items-center mb-1">
                      <span>{method.label}</span>
                      {isUserPreferred && showUserPreferenceHint && (
                        <span className="ml-1 text-xs">⭐</span>
                      )}
                    </div>
                    {!method.available && (
                      <span className="text-xs text-ios-gray dark:text-ios-dark-gray">
                        {t('v3/tasks:editors.channelSelector.comingSoon')}
                      </span>
                    )}
                    {isSelected && method.available && (
                      <div className="absolute top-1 right-1">
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* 选择状态提示 */}
          <div className="mt-4 p-3 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md">
            <div className="text-ios-subhead">
              <span className="text-ios-gray dark:text-ios-dark-gray">{t('v3/tasks:editors.channelSelector.selectedLabel')}</span>
              <span className="font-medium ml-1">
                {tempSelectedChannels.length === 0
                  ? t('v3/tasks:editors.channelSelector.none')
                  : tempSelectedChannels
                      .map(channel => {
                        const method = reminderMethods.find(m => m.value === channel);
                        return method?.label || channel;
                      })
                      .join('、')
                }
              </span>
            </div>
            {tempSelectedChannels.length === 0 && (
              <div className="text-ios-caption text-ios-red dark:text-ios-dark-red mt-1">
                {t('v3/tasks:editors.channelSelector.requireAtLeastOne')}
              </div>
            )}
          </div>
        </div>
    </BottomSheetModal>
  );
};

export default ChannelSelector;
